import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { localizedName } from '@/components/localized-name';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { useEvents } from '@/features/events/events.api';
import { EVENT_TYPES } from '@/features/memberships/membership-enums';
import {
  useBookingLocations,
  useServiceTypes,
} from '@/features/memberships/memberships.api';
import type { RestrictionDraft } from './voucher-format';

/**
 * "Can be used on": the four restriction pickers, shared by the catalogue
 * editor and the issue-a-custom-voucher form so the two can't drift apart.
 */
export function RestrictionPickers({
  value,
  onChange,
  enabled,
}: {
  value: RestrictionDraft;
  onChange: (next: RestrictionDraft) => void;
  /** Loads the pick lists only while the surrounding sheet is open. */
  enabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage ?? 'en';

  const locations = useBookingLocations(enabled);
  const serviceTypes = useServiceTypes(enabled);
  const events = useEvents();

  const locationOptions = useMemo(
    () =>
      (locations.data ?? []).map((location) => ({
        value: location.id,
        label: localizedName(location, language) || location.name || '—',
      })),
    [locations.data, language]
  );
  const serviceTypeOptions = useMemo(
    () =>
      (serviceTypes.data ?? []).map((type) => ({
        value: type.id,
        label: localizedName(type, language) || '—',
      })),
    [serviceTypes.data, language]
  );
  const eventTypeOptions = useMemo(
    () =>
      // 5 is "None" — not something an event can be.
      EVENT_TYPES.filter((type) => type.value !== 5).map((type) => ({
        value: String(type.value),
        label: t(type.labelKey),
      })),
    [t]
  );
  const eventOptions = useMemo(
    () =>
      (events.data ?? [])
        .map((event) => ({
          value: event.id,
          label: localizedName(event, language) || '—',
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [events.data, language]
  );

  const unrestricted =
    value.locationIds.length === 0 &&
    value.serviceTypeIds.length === 0 &&
    value.eventTypes.length === 0 &&
    value.eventIds.length === 0;

  const summarise = (count: number) => t('vouchers.selectedCount', { count });

  const pickers = [
    {
      key: 'locationIds' as const,
      label: t('vouchers.locations'),
      allLabel: t('vouchers.anyLocation'),
      options: locationOptions,
      loading: locations.isPending,
    },
    {
      key: 'serviceTypeIds' as const,
      label: t('vouchers.serviceTypes'),
      allLabel: t('vouchers.anyServiceType'),
      options: serviceTypeOptions,
      loading: serviceTypes.isPending,
    },
    {
      key: 'eventTypes' as const,
      label: t('vouchers.eventTypes'),
      allLabel: t('vouchers.anyEventType'),
      options: eventTypeOptions,
      loading: false,
    },
    {
      key: 'eventIds' as const,
      label: t('vouchers.events'),
      allLabel: t('vouchers.anyEvent'),
      options: eventOptions,
      loading: events.isPending,
    },
  ];

  return (
    <div className="space-y-4 rounded-lg border p-3">
      <div>
        <p className="text-sm font-semibold">{t('vouchers.restrictions')}</p>
        <p className="text-muted-foreground text-xs">
          {unrestricted
            ? t('vouchers.restrictionsNone')
            : t('vouchers.restrictionsHint')}
        </p>
      </div>

      {pickers.map((picker) => (
        <div key={picker.key} className="space-y-1.5">
          <Label className="text-xs font-semibold">{picker.label}</Label>
          <MultiSelect
            options={picker.options}
            selected={value[picker.key]}
            onChange={(selected) => onChange({ ...value, [picker.key]: selected })}
            disabled={picker.loading}
            heading={picker.label}
            allLabel={picker.loading ? t('common.loading') : picker.allLabel}
            summarise={summarise}
            matchTriggerWidth
          />
        </div>
      ))}
    </div>
  );
}
